import { faIdCard } from '@fortawesome/pro-regular-svg-icons/faIdCard'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle'
import { Box, Flex, Grid, TextInput, Text, Select } from '@mantine/core'
import { useMemo } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreCustomerNature, type UNSAFE_any } from '@flecto/types'
import { CollapsibleCard, Icon, InputPhoneNumber } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { useFormContext } from './FormCustomer'

export function BasicInfo() {
  const { t } = useTranslation('saas', { keyPrefix: 'customers' })
  const form = useFormContext()
  const { isMobile } = useMediaQuery()

  const getErrorMessage = (message: UNSAFE_any): string | false =>
    message === 'at_least_one' ? false : message

  const errorValidation = useMemo(() => {
    const requiredFields = ['email', 'phone_number', 'vat_number']
    return requiredFields.every(
      (field) => form.errors[field] === 'at_least_one'
    )
  }, [form.errors])

  return (
    <Box mt='md' mb='lg'>
      <CollapsibleCard opened>
        <CollapsibleCard.Title>
          {t('add_customer.basic_info')}
        </CollapsibleCard.Title>
        <CollapsibleCard.Description>
          {t('add_customer.basic_info_description')}
        </CollapsibleCard.Description>
        <CollapsibleCard.Icon withCircleBackground icon={faIdCard} />

        <CollapsibleCard.Content>
          <Box>
            <Grid>
              <Grid.Col span={isMobile ? 12 : 8}>
                <TextInput
                  withAsterisk
                  mb='md'
                  aria-label='name-input'
                  label={t('add_customer.name')}
                  placeholder={t('add_customer.name_placeholder')}
                  {...form.getInputProps('full_name')}
                />
              </Grid.Col>
              <Grid.Col span={isMobile ? 12 : 4}>
                <Select
                  label={t('add_customer.customer_type')}
                  placeholder={t('add_customer.customer_type_placeholder')}
                  data={[
                    { value: 'personal', label: t('add_customer.individual') },
                    { value: 'business', label: t('add_customer.business') },
                  ]}
                  {...form.getInputProps('nature')}
                  onChange={(value) => {
                    form.setFieldValue('nature', value as CoreCustomerNature)
                  }}
                />
              </Grid.Col>
            </Grid>
            <Grid mt='sm'>
              <Grid.Col span={isMobile ? 12 : 4}>
                <TextInput
                  mb='md'
                  aria-label='email-input'
                  label={t('add_customer.email_address')}
                  placeholder={t('add_customer.email_address_placeholder')}
                  {...form.getInputProps('email')}
                  error={
                    errorValidation
                      ? errorValidation
                      : getErrorMessage(form.errors.email)
                  }
                />
              </Grid.Col>
              <Grid.Col span={isMobile ? 12 : 4}>
                <InputPhoneNumber
                  label={t('add_customer.phone_number')}
                  onChangeDialCode={(code: string) =>
                    form.setFieldValue('phone_number_country_code', code)
                  }
                  valueDialCode={form.values.phone_number_country_code}
                  {...form.getInputProps('phone_number')}
                  error={
                    errorValidation
                      ? errorValidation
                      : getErrorMessage(form.errors.phone_number)
                  }
                />
              </Grid.Col>
              <Grid.Col span={isMobile ? 12 : 4}>
                <TextInput
                  mb='md'
                  aria-label='vat-number-input'
                  label={t('add_customer.vat_number')}
                  placeholder={t('add_customer.vat_number_placeholder')}
                  {...form.getInputProps('vat_number')}
                  error={
                    errorValidation
                      ? errorValidation
                      : getErrorMessage(form.errors.vat_number)
                  }
                />
              </Grid.Col>
            </Grid>
            <Flex
              mt='xs'
              mb='xs'
              gap='xs'
              align={'center'}
              c={errorValidation ? 'red' : 'gray'}
              wrap='wrap'
            >
              <Icon icon={faInfoCircle} />
              <Text size='sm' style={{ whiteSpace: 'nowrap' }}>
                {t('add_customer.basic_info_alert_fill_in')}
              </Text>
              <Text size='sm' fw='bold' style={{ whiteSpace: 'nowrap' }}>
                {t('add_customer.basic_info_alert_at_least')}
              </Text>
              <Text size='sm' style={{ whiteSpace: 'nowrap' }}>
                {t('add_customer.basic_info_alert_fields_above')}
              </Text>
            </Flex>
          </Box>
        </CollapsibleCard.Content>
      </CollapsibleCard>
    </Box>
  )
}
